import React, { useState } from 'react';
import { X, User, Phone, Mail, MapPin, Loader2, Search, FileText } from 'lucide-react';
import { clientService } from '../services/api';
import toast from 'react-hot-toast';

const QuickClientModal = ({ onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [loadingCep, setLoadingCep] = useState(false);
    const [formData, setFormData] = useState({
        full_name: '',
        apelido: '',
        cpf: '',
        email: '',
        phone: '',
    });

    const [addressFields, setAddressFields] = useState({
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddressFieldChange = (field, value) => {
        setAddressFields(prev => ({ ...prev, [field]: value }));
    };

    const maskCep = (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        return numbers.replace(/(\d{5})(\d)/, '$1-$2').replace(/(-\d{3})\d+?$/, '$1');
    };

    const maskCpfCnpj = (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return numbers.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else {
            return numbers.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d{1,2})$/, '$1-$2');
        }
    };

    const fetchCep = async (cep) => {
        const cleanCep = cep.replace(/\D/g, '');
        if (cleanCep.length !== 8) {
            toast.error('CEP deve conter 8 dígitos');
            return;
        }

        setLoadingCep(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '/api';
            const response = await fetch(`${apiUrl}/utils/cep/${cleanCep}`);
            const data = await response.json();

            if (data.erro) {
                toast.error('CEP não encontrado');
                return;
            }

            setAddressFields(prev => ({
                ...prev,
                street: data.logradouro || '',
                neighborhood: data.bairro || '',
                city: data.localidade || '',
                state: data.uf || '',
                complement: data.complemento || ''
            }));

            toast.success('CEP encontrado!');
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
            toast.error('Erro ao buscar CEP');
        } finally {
            setLoadingCep(false);
        }
    };

    const buildFullAddress = () => {
        const parts = [];
        if (addressFields.street) parts.push(addressFields.street);
        if (addressFields.number) parts.push(`nº ${addressFields.number}`);
        if (addressFields.complement) parts.push(addressFields.complement);
        if (addressFields.neighborhood) parts.push(addressFields.neighborhood);
        if (addressFields.city) parts.push(addressFields.city);
        if (addressFields.state) parts.push(addressFields.state);
        if (addressFields.cep) parts.push(`CEP: ${addressFields.cep}`);

        return parts.join(', ');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.full_name) {
            toast.error('Nome do cliente é obrigatório');
            return;
        }

        if (!formData.apelido) {
            toast.error('Apelido é obrigatório');
            return;
        }

        // Validar CPF (opcional - se fornecido, deve ter 11 ou 14 dígitos)
        if (formData.cpf) {
            const cpfNumbers = formData.cpf.replace(/\D/g, '');
            if (cpfNumbers.length !== 11 && cpfNumbers.length !== 14) {
                toast.error('CPF ou CNPJ inválido (deve ter 11 ou 14 dígitos)');
                return;
            }
        }

        const fullAddress = buildFullAddress();

        try {
            setLoading(true);
            const response = await clientService.createClient({
                ...formData,
                cpf: formData.cpf || null,
                address: fullAddress
            });
            toast.success('Cliente cadastrado com sucesso!');
            if (onSuccess) {
                onSuccess(response.data.client);
            }
            onClose();
        } catch (error) {
            console.error('Erro ao cadastrar cliente:', error);
            toast.error(error.response?.data?.message || 'Erro ao cadastrar cliente');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden flex flex-col border border-secondary-200 animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-secondary-100 bg-secondary-50/50">
                    <div className="flex items-center gap-2">
                        <div className="bg-primary-100 p-1.5 rounded-md">
                            <User className="w-4 h-4 text-primary-700" />
                        </div>
                        <h3 className="font-bold text-secondary-900">Cadastro Rápido de Cliente</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-full transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[75vh]">
                    {/* Dados Básicos */}
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-secondary-400">Identificação</h4>
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-secondary-700">Nome Completo *</label>
                            <div className="relative">
                                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                                <input
                                    type="text"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    className="input pl-9"
                                    placeholder="Nome do cliente"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-secondary-700">Apelido *</label>
                            <div className="relative">
                                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                                <input
                                    type="text"
                                    name="apelido"
                                    value={formData.apelido}
                                    onChange={handleChange}
                                    className="input pl-9"
                                    placeholder="Como o cliente será chamado"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-secondary-700">CPF/CNPJ</label>
                            <div className="relative">
                                <FileText className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                                <input
                                    type="text"
                                    name="cpf"
                                    value={formData.cpf}
                                    onChange={(e) => setFormData(prev => ({ ...prev, cpf: maskCpfCnpj(e.target.value) }))}
                                    className="input pl-9"
                                    placeholder="CPF ou CNPJ"
                                    maxLength={18}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-secondary-700">Telefone</label>
                                <div className="relative">
                                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="input pl-9"
                                        placeholder="(00) 00000-0000"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-secondary-700">E-mail</label>
                                <div className="relative">
                                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="input pl-9"
                                        placeholder="exemplo@email.com"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Endereço */}
                    <div className="space-y-4 pt-2">
                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-secondary-400">Endereço</h4>

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-secondary-700 text-sm mb-1 block">CEP</label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                                    <input
                                        type="text"
                                        value={addressFields.cep}
                                        onChange={(e) => setAddressFields(prev => ({ ...prev, cep: maskCep(e.target.value) }))}
                                        onBlur={() => addressFields.cep.replace(/\D/g, '').length === 8 && fetchCep(addressFields.cep)}
                                        className="input pl-9"
                                        placeholder="00000-000"
                                        maxLength={9}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => fetchCep(addressFields.cep)}
                                    disabled={loadingCep || addressFields.cep.replace(/\D/g, '').length !== 8}
                                    className="px-4 py-2 bg-secondary-900 text-white rounded-md hover:bg-black transition-colors disabled:opacity-50 text-xs font-bold"
                                >
                                    {loadingCep ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="md:col-span-3 space-y-1">
                                <label className="text-xs font-medium text-secondary-700">Rua/Logradouro</label>
                                <input
                                    type="text"
                                    value={addressFields.street}
                                    onChange={(e) => handleAddressFieldChange('street', e.target.value)}
                                    className="input"
                                    placeholder="Nome da rua"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-secondary-700">Número</label>
                                <input
                                    type="text"
                                    value={addressFields.number}
                                    onChange={(e) => handleAddressFieldChange('number', e.target.value)}
                                    className="input"
                                    placeholder="123"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-secondary-700">Bairro</label>
                                <input
                                    type="text"
                                    value={addressFields.neighborhood}
                                    onChange={(e) => handleAddressFieldChange('neighborhood', e.target.value)}
                                    className="input"
                                    placeholder="Bairro"
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div className="col-span-2 space-y-1">
                                    <label className="text-xs font-medium text-secondary-700">Cidade</label>
                                    <input
                                        type="text"
                                        value={addressFields.city}
                                        onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                                        className="input"
                                        placeholder="Cidade"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-secondary-700">UF</label>
                                    <input
                                        type="text"
                                        value={addressFields.state}
                                        onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                                        className="input uppercase"
                                        maxLength={2}
                                        placeholder="SP"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary px-6"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary px-6 flex items-center gap-2"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            Salvar Cliente
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default QuickClientModal;
